import { type FaceDetectionResponse, type ObjectDetectionResponse } from "@yujacorp/inference-ambassador";
import type { HumanFaceDetection, TfjsCocoObjectDetection } from "@yujacorp/verity-machine-learning";

import apiClient from "../../../../../helpers/api-client";
import { useEffect, useState } from "react";

export type VersionedFaceDetection = {
	timeFromStart: number;
} & (
	| ({
			version: "v1";
	  } & HumanFaceDetection)
	| ({
			version: "v2";
	  } & FaceDetectionResponse)
);

export type VersionedObjectDetection = {
	timeFromStart: number;
} & (
	| ({
			version: "v1";
	  } & TfjsCocoObjectDetection)
	| ({
			version: "v2";
	  } & ObjectDetectionResponse)
);

export const useMachineLearningEventsFromDataDump = (proctoringSessionUuid?: string, startDate?: Date) => {
	const [faceDetections, setFaceDetections] = useState<VersionedFaceDetection[]>([]);
	const [objectDetections, setObjectDetections] = useState<VersionedObjectDetection[]>([]);

	useEffect(() => {
		async function fetchData() {
			if (!proctoringSessionUuid || !startDate) return;
			const { links } = await apiClient.dataDump.getLinks({ pathParams: { proctoringSessionUuid } });
			const { faceLinksV1, objectLinksV1, faceLinksV2, objectLinksV2 } = {
				faceLinksV1: links.filter((link) => link.source === "machine-learning-face-detection"),
				objectLinksV1: links.filter((link) => link.source === "machine-learning-object-detection"),
				faceLinksV2: links.filter((link) => link.source === "machine-learning-face-detection-v2"),
				objectLinksV2: links.filter((link) => link.source === "machine-learning-object-detection-v2"),
			};

			if (faceLinksV2.length > 0) {
				const faceDetectionDumps = (await Promise.all(
					faceLinksV2.map((link) => fetch(link.downloadUrl).then((response) => response.json())),
				)) as FaceDetectionResponse[][];

				const faceDetections = faceDetectionDumps.flat().map(
					(detection) =>
						({
							version: "v2",
							...detection,
							timeFromStart: (detection.image_metadata.timestamp - startDate.getTime()) / 1000,
						}) satisfies VersionedFaceDetection,
				);

				setFaceDetections(faceDetections);
			} else {
				const faceDetectionDumps = (await Promise.all(
					faceLinksV1.map((link) => fetch(link.downloadUrl).then((response) => response.json())),
				)) as HumanFaceDetection[][];

				const faceDetections = faceDetectionDumps.flat().map(
					(detection) =>
						({
							version: "v1",
							...detection,
							timeFromStart: (detection.timing.sourceReceivedAt - startDate.getTime()) / 1000,
						}) satisfies VersionedFaceDetection,
				);

				setFaceDetections(faceDetections);
			}

			if (objectLinksV2.length > 0) {
				const objectDetectionDumps = (await Promise.all(
					objectLinksV2.map((link) => fetch(link.downloadUrl).then((response) => response.json())),
				)) as ObjectDetectionResponse[][];

				const objectDetections = objectDetectionDumps.flat().map(
					(detection) =>
						({
							version: "v2",
							...detection,
							timeFromStart: (detection.image_metadata.timestamp - startDate.getTime()) / 1000,
						}) satisfies VersionedObjectDetection,
				);

				setObjectDetections(objectDetections);
			} else {
				const objectDetectionDumps = (await Promise.all(
					objectLinksV1.map((link) => fetch(link.downloadUrl).then((response) => response.json())),
				)) as TfjsCocoObjectDetection[][];

				const objectDetections = objectDetectionDumps.flat().map(
					(detection) =>
						({
							version: "v1",
							...detection,
							timeFromStart: (detection.timing.sourceReceivedAt - startDate.getTime()) / 1000,
						}) satisfies VersionedObjectDetection,
				);

				setObjectDetections(objectDetections);
			}
		}

		void fetchData();
	}, [proctoringSessionUuid, startDate]);

	return {
		faceDetections,
		objectDetections,
	};
};

export const useMachineLearningAspectRatio = (
	faceDetections: VersionedFaceDetection[],
	objectDetections: VersionedObjectDetection[],
) => {
	const hasFaceDetections = faceDetections.length > 0;
	const hasObjectDetections = objectDetections.length > 0;

	if (!hasFaceDetections && !hasObjectDetections) {
		return { aspectRatio: 16 / 9 };
	}

	const getV1AspectRatio = () => {
		const faceRatios = faceDetections
			.filter((d) => d.version === "v1" && !d.isEmpty)
			.map((d) => d.data[0].source.width / d.data[0].source.height);

		const objectRatios = objectDetections
			.filter((d) => d.version === "v1" && !d.isEmpty)
			.map((d) => d.data[0].source.width / d.data[0].source.height);

		if (faceRatios.length === 0 && objectRatios.length === 0) {
			return undefined;
		}

		return { aspectRatio: Math.max(...faceRatios, ...objectRatios) };
	};

	const getV2AspectRatio = () => {
		const faceRatios = faceDetections
			.filter((d) => d.version === "v2")
			.map((d) => d.image_metadata.width / d.image_metadata.height);

		const objectRatios = objectDetections
			.filter((d) => d.version === "v2")
			.map((d) => d.image_metadata.width / d.image_metadata.height);

		if (faceRatios.length === 0 && objectRatios.length === 0) {
			return undefined;
		}

		return { aspectRatio: Math.max(...faceRatios, ...objectRatios) };
	};

	const finalAspectRatio = getV1AspectRatio()?.aspectRatio ?? getV2AspectRatio()?.aspectRatio ?? 16 / 9;

	return { aspectRatio: finalAspectRatio };
};

export const useMachineLearningNearestDetection = (
	{ faceDetections, objectDetections }: ReturnType<typeof useMachineLearningEventsFromDataDump>,
	globaltime: number,
) => {
	const getDetectionWithinThreshold = <T extends { timeFromStart: number }>(detections: T[], globaltime: number) => {
		if (detections.length === 0) {
			return undefined;
		}
		return detections.find((detection) => {
			return Math.abs(globaltime - detection.timeFromStart) < 0.5;
		});
	};

	const getDetectionClosestToGlobaltime = <T extends { timeFromStart: number }>(
		detections: T[],
		globaltime: number,
	) => {
		if (detections.length === 0) {
			return undefined;
		}
		return detections.reduce((nearest, detection) => {
			const currentDiff = Math.abs(globaltime - detection.timeFromStart);
			const nearestDiff = Math.abs(globaltime - nearest.timeFromStart);
			return currentDiff < nearestDiff ? detection : nearest;
		}, detections[0]);
	};

	const getBestDetection = <T extends { timeFromStart: number }>(detections: T[], globaltime: number) => {
		return (
			getDetectionWithinThreshold(detections, globaltime) || getDetectionClosestToGlobaltime(detections, globaltime)
		);
	};

	const nearestFaceDetection = getBestDetection(faceDetections, globaltime);
	const nearestObjectDetection = getBestDetection(objectDetections, globaltime);
	return { nearestFaceDetection, nearestObjectDetection };
};
